/**
 * Bidirectional sync between the Details form and the Calendar panel.
 */
import { useCallback, useEffect, useRef } from 'react';
import type { FormBuilderHandle, CalendarHandle } from '@kula-ai/components';
import type { ScheduleSharedState } from '../state/useScheduleSharedState';
import moment from 'moment-timezone';
import {
  formatTime12,
  buildIsoFromDateAndTime,
  addMinutesToIso,
  diffMinutesIso,
  parse12hrTime,
} from '../steps/details/fields/dateTime';

/** Hard ceiling on a single slot's duration — 24 hours, matching legacy
 *  prod and the duration field's own `> 1440` validation. End-date /
 *  end-time edits that would exceed this are rejected. */
const MAX_DURATION_MINUTES = 1440;

/** Scroll the booked slot into view by scrolling the calendar's fc-scroller. */
function scrollBookedSlotIntoView() {
  setTimeout(() => {
    const bookedEl = document.querySelector('.im-calendar .booked-event');
    if (bookedEl) {
      bookedEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, 200);
}

interface UseFormCalendarSyncOptions {
  formRef: React.RefObject<FormBuilderHandle | null>;
  sharedState: ScheduleSharedState;
  updateSharedState: (_partial: Partial<ScheduleSharedState>) => void;
  calendarRef?: React.RefObject<CalendarHandle | null>;
}

export function useFormCalendarSync({
  formRef,
  sharedState,
  updateSharedState,
  calendarRef,
}: UseFormCalendarSyncOptions) {
  const skipSyncRef = useRef(false);
  const timezone = sharedState.timezone;

  // ---- Form → Shared State ----
  const handleFieldChange = useCallback(
    (name: string, value: unknown, _allValues: Record<string, unknown>) => {
      if (skipSyncRef.current) return;

      const form = formRef.current;
      const duration = sharedState.bookedSlot?.duration ?? 30;

      switch (name) {
        case 'interviewName':
          updateSharedState({ interviewName: value as string });
          break;

        case 'startDate': {
          const vals = form?.getValues() as Record<string, unknown> | undefined;
          let startTimeStr = (vals?.startTime as string) || '';
          if (!startTimeStr && sharedState.bookedSlot?.start) {
            startTimeStr = formatTime12(sharedState.bookedSlot.start, timezone);
          }
          if (!value || !startTimeStr) break;

          // Reject past dates
          const selectedDate = moment(value as Date)
            .tz(timezone)
            .startOf('day');
          const today = moment().tz(timezone).startOf('day');
          if (selectedDate.isBefore(today)) {
            break;
          }

          // If switching to today and current startTime is in the past, reject
          if (selectedDate.isSame(today, 'day')) {
            const parsed = parse12hrTime(startTimeStr);
            if (parsed) {
              const nowMins =
                moment().tz(timezone).hours() * 60 +
                moment().tz(timezone).minutes();
              const selectedMins = parsed.hours * 60 + parsed.minutes;
              if (selectedMins <= nowMins) {
                // Revert date to previous value
                if (sharedState.bookedSlot?.start) {
                  skipSyncRef.current = true;
                  form?.setValue(
                    'startDate',
                    new Date(sharedState.bookedSlot.start)
                  );
                  skipSyncRef.current = false;
                }
                break;
              }
            }
          }

          const newStart = buildIsoFromDateAndTime(
            value as Date,
            startTimeStr,
            timezone
          );
          if (!newStart) break;
          const newEnd = addMinutesToIso(newStart, duration);
          const endTimeStr = formatTime12(newEnd, timezone);
          updateSharedState({
            bookedSlot: { start: newStart, end: newEnd, duration },
          });
          skipSyncRef.current = true;
          // Use the formatted end directly. `useTimeOptions` injects
          // the same string into `endTimeOptions` when it's off the
          // 15-min grid, so the Select finds an exact match and the
          // sub-15 duration survives. `findClosestTimeOption` here
          // would silently snap 07:31 → 07:30 (back to startTime),
          // the Select would then have no matching option, and the
          // dropdown would render blank.
          form?.setValue('endTime', endTimeStr);
          // Keep End date in lockstep — shifting the start moves the
          // whole (duration-preserving) slot, so the end date tracks
          // newEnd, which may roll to the next day for a slot whose
          // duration crosses midnight.
          form?.setValue('endDate', new Date(newEnd));
          form?.setValue('dateTime', `${newStart}|${newEnd}`);
          skipSyncRef.current = false;
          calendarRef?.current?.goToDate(value as Date);
          scrollBookedSlotIntoView();
          break;
        }

        case 'startTime': {
          const vals = form?.getValues() as Record<string, unknown> | undefined;
          // startDate may be null if the date field was conditionally unmounted
          // Fall back to deriving from dateTime string
          let startDate = vals?.startDate as Date | null;
          if (!startDate && sharedState.bookedSlot?.start) {
            startDate = new Date(sharedState.bookedSlot.start);
          }
          if (!startDate || !value) break;

          // Reject past times if selected date is today
          const isToday = moment(startDate)
            .tz(timezone)
            .isSame(moment().tz(timezone), 'day');
          if (isToday) {
            const parsed = parse12hrTime(value as string);
            if (parsed) {
              const nowMins =
                moment().tz(timezone).hours() * 60 +
                moment().tz(timezone).minutes();
              const selectedMins = parsed.hours * 60 + parsed.minutes;
              if (selectedMins < nowMins) {
                // Revert to previous valid startTime
                const prevStartTime = formatTime12(
                  sharedState.bookedSlot?.start ?? '',
                  timezone
                );
                if (prevStartTime) {
                  skipSyncRef.current = true;
                  // No closest-snap — preserve the previous precise
                  // start (it may be an odd typed time like 03:12).
                  form?.setValue('startTime', prevStartTime);
                  skipSyncRef.current = false;
                }
                break;
              }
            }
          }

          const newStart = buildIsoFromDateAndTime(
            startDate,
            value as string,
            timezone
          );
          if (!newStart) break;
          const newEnd = addMinutesToIso(newStart, duration);
          const endTimeStr = formatTime12(newEnd, timezone);
          updateSharedState({
            bookedSlot: { start: newStart, end: newEnd, duration },
          });
          skipSyncRef.current = true;
          // Same as the startDate branch — `endTimeStr` is already the
          // formatted end (matches the injected option in
          // `useTimeOptions`); snapping to the nearest 15-min grid
          // option here would erase the recruiter's sub-15 duration.
          form?.setValue('endTime', endTimeStr);
          // End date tracks newEnd (duration-preserving shift; may roll
          // to the next day when the slot crosses midnight).
          form?.setValue('endDate', new Date(newEnd));
          form?.setValue('dateTime', `${newStart}|${newEnd}`);
          skipSyncRef.current = false;
          scrollBookedSlotIntoView();
          break;
        }

        case 'endTime': {
          const vals = form?.getValues() as Record<string, unknown> | undefined;
          let startDate = vals?.startDate as Date | null;
          if (!startDate && sharedState.bookedSlot?.start) {
            startDate = new Date(sharedState.bookedSlot.start);
          }
          // End date falls back to start date for same-day slots.
          const endDate = (vals?.endDate as Date | null) ?? startDate;
          const startTimeStr = (vals?.startTime as string) || '';
          if (!startDate || !endDate || !startTimeStr || !value) break;

          const startIso = buildIsoFromDateAndTime(
            startDate,
            startTimeStr,
            timezone
          );
          // Build end from the END date — a slot ending 02:45 PM on a
          // later day is valid even though 02:45 < 03:15 as clock times.
          const endIso = buildIsoFromDateAndTime(
            endDate,
            value as string,
            timezone
          );
          if (!startIso || !endIso) break;
          const newDuration = diffMinutesIso(startIso, endIso);
          if (newDuration <= 0) {
            // end <= start as a full datetime (same-day end before
            // start) — revert to the previous end time.
            const prevEndTime = formatTime12(
              sharedState.bookedSlot?.end ?? '',
              timezone
            );
            if (prevEndTime) {
              skipSyncRef.current = true;
              form?.setValue('endTime', prevEndTime);
              skipSyncRef.current = false;
            }
            break;
          }
          // Legacy: a > 24h slot caps duration at 1440 and slides the
          // START forward to honour the chosen end (`start = end − dur`),
          // so the Start date/time field stays in sync. We keep exact
          // (no 15-min snap) so SCH-39 odd times round-trip.
          if (newDuration > MAX_DURATION_MINUTES) {
            const cappedStart = addMinutesToIso(endIso, -MAX_DURATION_MINUTES);
            updateSharedState({
              bookedSlot: {
                start: cappedStart,
                end: endIso,
                duration: MAX_DURATION_MINUTES,
              },
            });
            skipSyncRef.current = true;
            form?.setValue('startDate', new Date(cappedStart));
            form?.setValue('startTime', formatTime12(cappedStart, timezone));
            form?.setValue('duration', MAX_DURATION_MINUTES);
            form?.setValue('dateTime', `${cappedStart}|${endIso}`);
            skipSyncRef.current = false;
            break;
          }
          // Within 24h — keep the start, recompute duration exactly
          // (`endTimeOptions` injects the precise/booked end so the
          // Select can render an off-grid value; snapping to 15 here
          // would destroy a recruiter's sub-15 duration).
          updateSharedState({
            bookedSlot: {
              start: startIso,
              end: endIso,
              duration: newDuration,
            },
          });
          skipSyncRef.current = true;
          form?.setValue('duration', newDuration);
          form?.setValue('dateTime', `${startIso}|${endIso}`);
          skipSyncRef.current = false;
          break;
        }

        case 'endDate': {
          const vals = form?.getValues() as Record<string, unknown> | undefined;
          let startDate = vals?.startDate as Date | null;
          if (!startDate && sharedState.bookedSlot?.start) {
            startDate = new Date(sharedState.bookedSlot.start);
          }
          const startTimeStr = (vals?.startTime as string) || '';
          const endTimeStr = (vals?.endTime as string) || '';
          if (!startDate || !value || !startTimeStr || !endTimeStr) break;

          const startIso = buildIsoFromDateAndTime(
            startDate,
            startTimeStr,
            timezone
          );
          const endIso = buildIsoFromDateAndTime(
            value as Date,
            endTimeStr,
            timezone
          );
          if (!startIso || !endIso) break;
          const newDuration = diffMinutesIso(startIso, endIso);
          if (newDuration <= 0) {
            // End date before start (picker minDate should prevent this;
            // safety net) — revert to the previous end date.
            if (sharedState.bookedSlot?.end) {
              skipSyncRef.current = true;
              form?.setValue('endDate', new Date(sharedState.bookedSlot.end));
              skipSyncRef.current = false;
            }
            break;
          }
          // Legacy: pushing the end date past 24h caps the duration and
          // slides the START forward to keep the slot ≤ 24h with the
          // chosen end (`start = end − 1440`). Start date/time sync.
          if (newDuration > MAX_DURATION_MINUTES) {
            const cappedStart = addMinutesToIso(endIso, -MAX_DURATION_MINUTES);
            updateSharedState({
              bookedSlot: {
                start: cappedStart,
                end: endIso,
                duration: MAX_DURATION_MINUTES,
              },
            });
            skipSyncRef.current = true;
            form?.setValue('startDate', new Date(cappedStart));
            form?.setValue('startTime', formatTime12(cappedStart, timezone));
            form?.setValue('duration', MAX_DURATION_MINUTES);
            form?.setValue('dateTime', `${cappedStart}|${endIso}`);
            skipSyncRef.current = false;
            break;
          }
          updateSharedState({
            bookedSlot: {
              start: startIso,
              end: endIso,
              duration: newDuration,
            },
          });
          skipSyncRef.current = true;
          form?.setValue('duration', newDuration);
          form?.setValue('dateTime', `${startIso}|${endIso}`);
          skipSyncRef.current = false;
          break;
        }

        case 'duration': {
          const newDuration = value as number;
          if (!sharedState.bookedSlot?.start) {
            // No slot yet — still remember the duration so the calendar
            // overlay knows what length of slots to offer.
            updateSharedState({
              duration: newDuration,
              bookedSlot: sharedState.bookedSlot
                ? { ...sharedState.bookedSlot, duration: newDuration }
                : null,
            });
            break;
          }
          const newEnd = addMinutesToIso(
            sharedState.bookedSlot.start,
            newDuration
          );
          const endTimeStr = formatTime12(newEnd, timezone);
          updateSharedState({
            duration: newDuration,
            bookedSlot: {
              ...sharedState.bookedSlot,
              duration: newDuration,
              end: newEnd,
            },
          });
          skipSyncRef.current = true;
          // See above — formatted end goes in directly; the Select
          // matches via the injected option from `useTimeOptions`.
          form?.setValue('endTime', endTimeStr);
          // End date follows the duration-driven end (crosses midnight
          // for a long-enough duration).
          form?.setValue('endDate', new Date(newEnd));
          form?.setValue(
            'dateTime',
            `${sharedState.bookedSlot.start}|${newEnd}`
          );
          skipSyncRef.current = false;
          break;
        }

        // --- Other field syncs ---
        case 'participant_ids': {
          const ids = Array.isArray(value)
            ? (value as string[]).map(Number)
            : value
              ? [Number(value)]
              : [];
          updateSharedState({ interviewerIds: ids });
          break;
        }
        case 'interview_type': {
          // Keep the calendar's union/intersection mode in sync with
          // the form — switching panel ↔ one-on-one mid-edit otherwise
          // leaves sharedState.interviewKind stale and the availability
          // algorithm picks the wrong strategy.
          if (value === 'panel' || value === 'one_on_one') {
            updateSharedState({ interviewKind: value });
          }
          break;
        }
        case 'meeting_room_ids': {
          // The field can be single (string email) or multi (string[]) —
          // normalise to string[] for downstream consumers. Empty or
          // non-string values drop so the room-busy fetch stays clean.
          const raw = Array.isArray(value) ? value : [value];
          const emails = raw.filter(
            (v): v is string => typeof v === 'string' && v.length > 0
          );
          updateSharedState({ meetingRoomEmails: emails });
          break;
        }
        case 'location':
          updateSharedState({ location: value as string });
          break;
        case 'host_id':
          updateSharedState({ hostId: value ? Number(value) : null });
          break;
        case 'calendar_visibility':
          updateSharedState({
            calendarVisibility: value ? 'private' : 'default',
          });
          break;
        case 'enable_note_taker':
          updateSharedState({ noteTakerEnabled: Boolean(value) });
          break;
      }
    },
    [formRef, sharedState.bookedSlot, updateSharedState, calendarRef, timezone]
  );

  // ---- Calendar → Form: when bookedSlot changes, update all form fields ----
  useEffect(() => {
    const form = formRef.current;
    if (!form || !sharedState.bookedSlot) return;

    skipSyncRef.current = true;
    try {
      const { start, end, duration } = sharedState.bookedSlot;
      if (start && end) {
        // Both formatted directly — NO closest-snap. FullCalendar
        // drag/click is already 15-min aligned (formatTime12 yields the
        // exact grid string there), and typed/edited odd times (e.g.
        // 03:10) must survive the round-trip. `useTimeOptions` injects
        // the precise start AND end into their option lists so the
        // creatable Selects can render them — snapping start → nearest
        // 15-min silently rewrote 03:10 → 03:15 (slot ISO stayed
        // correct, field disagreed).
        const startTimeStr = formatTime12(start, timezone);
        const endTimeStr = formatTime12(end, timezone);
        form.setValue('dateTime', `${start}|${end}`);
        form.setValue('startDate', new Date(start));
        // Hydrate End date from the end ISO independently — this is what
        // round-trips a multi-day slot (start 22nd, end 23rd) into the
        // form instead of collapsing both onto the start date.
        form.setValue('endDate', new Date(end));
        form.setValue('startTime', startTimeStr);
        form.setValue('endTime', endTimeStr);
      }
      if (duration) {
        form.setValue('duration', duration);
      }
    } finally {
      skipSyncRef.current = false;
    }
  }, [formRef, sharedState.bookedSlot, timezone]);

  return { handleFieldChange };
}
